/**
 * CardLookupOverlay — global card art lookup.
 * Open with 'l' (when not in an input), double-click a card name, or long-press on mobile.
 * Shows card art, oracle text, and official rulings.
 * Close with Escape, backdrop click, or the × button.
 */

import { useEffect, useRef, useState } from 'react';
import { CardAutocomplete } from './CardAutocomplete';
import { cardImageUris } from '@/lib/utils';
import { cards } from '@/lib/api';
import type { CardDetail } from '@/lib/api';
import { renderManaSymbols } from '@/lib/text-pipeline';

interface CardLookupOverlayProps {
  open: boolean;
  onClose: () => void;
  /** Pre-populate the lookup with this card name when opened. */
  initialCard?: string;
}

export function CardLookupOverlay({ open, onClose, initialCard }: CardLookupOverlayProps) {
  const [query, setQuery] = useState('');
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [detail, setDetail] = useState<CardDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);

  // Auto-focus and reset when opened; pre-populate if initialCard provided
  useEffect(() => {
    if (open) {
      if (initialCard) {
        setQuery(initialCard);
        setSelectedCard(initialCard);
      } else {
        setQuery('');
        setSelectedCard(null);
        setDetail(null);
      }
      const t = setTimeout(() => {
        if (!initialCard) {
          inputRef.current?.querySelector('input')?.focus();
        }
      }, 30);
      return () => clearTimeout(t);
    }
  }, [open, initialCard]);

  // Fetch card detail whenever selectedCard changes
  useEffect(() => {
    if (!selectedCard) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    cards.getDetail(selectedCard).then(d => {
      if (!cancelled) {
        setDetail(d.found ? d : null);
        setDetailLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setDetailLoading(false);
    });
    return () => { cancelled = true; };
  }, [selectedCard]);

  // Escape closes
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  function handleChange(value: string) {
    setQuery(value);
    setSelectedCard(value || null);
  }

  if (!open) return null;

  const faces = selectedCard ? cardImageUris(selectedCard) : null;

  return (
    <div
      className="fixed inset-0 z-[2000] flex flex-col items-center justify-start"
      style={{ paddingTop: '8vh', paddingBottom: '5vh', background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      {/* Dialog panel */}
      <div
        className="w-full max-w-[500px] rounded-xl shadow-2xl flex flex-col"
        style={{ background: 'var(--surface-container-high)', border: '1px solid var(--outline-variant)', maxHeight: '84vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header — always visible */}
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-outline-variant shrink-0">
          <span className="material-icons text-on-surface-variant" style={{ fontSize: 20 }}>style</span>
          <div ref={inputRef} className="flex-1">
            <CardAutocomplete
              value={query}
              onChange={handleChange}
              placeholder="Look up a card…"
              inputClassName="w-full bg-transparent outline-none text-on-surface placeholder:text-on-surface-variant text-base py-0"
              wrapperClassName="w-full"
            />
          </div>
          <button
            onClick={onClose}
            className="text-on-surface-variant hover:text-on-surface transition-colors bg-transparent border-none cursor-pointer p-0.5 leading-none"
            title="Close (Esc)"
          >
            <span className="material-icons" style={{ fontSize: 18 }}>close</span>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto min-h-0 flex-1">
          {/* Card art — shown when a card is selected */}
          {faces && faces.length > 0 && (
            <div className="flex justify-center gap-3 pt-4 px-4">
              {faces.map((face, i) => (
                <img
                  key={i}
                  src={face.src}
                  alt={selectedCard ?? ''}
                  style={{
                    width: faces.length > 1 ? 200 : 280,
                    borderRadius: 12,
                    transform: face.rotate ? `rotate(${face.rotate}deg)` : undefined,
                    boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
                  }}
                />
              ))}
            </div>
          )}

          {/* Oracle text + rulings */}
          {selectedCard && (
            <div className="px-4 pb-4 pt-3 flex flex-col gap-3">
              {detailLoading && (
                <div className="text-xs text-on-surface-variant text-center py-2 animate-pulse">Loading…</div>
              )}

              {detail && !detailLoading && (
                <>
                  {/* Oracle text box */}
                  <div
                    className="rounded-md p-3 text-sm text-on-surface leading-relaxed"
                    style={{ background: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
                  >
                    {detail.type_line && (
                      <div className="text-xs text-on-surface-variant mb-1.5 font-medium">{detail.type_line}</div>
                    )}
                    {detail.oracle_text && detail.oracle_text.split('\n').map((line, i) => (
                      <p
                        key={i}
                        className="mb-1 last:mb-0"
                        dangerouslySetInnerHTML={{ __html: renderManaSymbols(line) }}
                      />
                    ))}
                    {detail.type_data?.power != null && detail.type_data.power !== '' && (
                      <div className="text-xs text-on-surface-variant mt-1.5 text-right font-mono">
                        {detail.type_data.power}/{detail.type_data.toughness ?? '?'}
                      </div>
                    )}
                  </div>

                  {/* Rulings */}
                  {detail.rulings.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <div className="text-xs text-on-surface-variant uppercase tracking-wider font-semibold px-0.5">
                        Rulings ({detail.rulings.length})
                      </div>
                      {detail.rulings.map((r, i) => (
                        <div
                          key={i}
                          className="rounded-md p-2.5 text-xs text-on-surface leading-relaxed"
                          style={{ background: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
                        >
                          <div className="text-on-surface-variant mb-1">{r.published_at}</div>
                          <div dangerouslySetInnerHTML={{ __html: renderManaSymbols(r.comment) }} />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer hint */}
        {!selectedCard && (
          <div
            className="flex items-center gap-4 px-4 py-1.5 border-t border-outline-variant text-xs text-on-surface-variant shrink-0"
            style={{ background: 'var(--surface-container)' }}
          >
            <span>type a card name to look it up</span>
            <span className="ml-auto opacity-60">press <kbd style={{ fontFamily: 'monospace' }}>l</kbd> anytime · <kbd style={{ fontFamily: 'monospace' }}>esc</kbd> close</span>
          </div>
        )}
      </div>
    </div>
  );
}
